import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import type {
  ExtendedThalamosUser,
  FormContextData,
  PatientTimelineResponse,
  UUID,
} from "@thalamos/aspire-common";
import { findFormsMerged } from "@thalamos/aspire-common";

import { routeFns } from "../../../routePaths.js";
import { PatientDemographicsOutdatedBanner } from "../../FormDraft/helpers/comparePatientDemographics.js";
import { mcaFormContextLogic } from "../helpers/formContextLogic.js";
import {
  FormContextPdfViewer,
  previewableForms,
} from "../helpers/FormContextPdfViewer.js";
import { ParticipantsList } from "../helpers/ParticipantsList.js";
import { WorkItemStatusCard } from "../helpers/WorkItemStatusCard.js";
import { StandaloneFormRow } from "../standalone/StandaloneFormRow.js";
import { McaContextualPrompt } from "./McaContextualPrompt.js";
import {
  Banner,
  BannerList,
  FormTitle,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/index.js";
import { PatientBanner } from "~/components/layout/index.js";
import { ExpandMore } from "~/components/layout/styleWrappers.js";
import { userCanEditMhaStatus } from "~/pages/EditMhaStatus/userCanEditMhaStatus.js";

/**
 * A combined Mental Capacity Act assessment: an FDMCA, a Best Interest Decision,
 * or both. Each form is worked on independently and gets its own row, while the
 * assessment as a whole is what gets sent for scrutiny.
 */
export function McaFormPage({
  formContext,
  user,
  patientTimeline,
  reloadFormContext,
  reloadPatientTimeline,
  onReconcileClick,
}: {
  formContext: FormContextData;
  user: ExtendedThalamosUser;
  patientTimeline: PatientTimelineResponse | null;
  reloadFormContext: () => void;
  reloadPatientTimeline: () => void;
  onReconcileClick?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pdfViewFormId, setPdfViewFormId] = useState<null | string>(null);
  const [showForms, setShowForms] = useState(true);

  const {
    forms,
    formStates,
    canFinaliseAssessment,
    promptTemplateId,
    canStartPromptedForm,
  } = mcaFormContextLogic(formContext, user);

  const merged = findFormsMerged(
    patientTimeline?.mergedPatientData,
    formContext,
  );

  const awaitingSignature = formStates.find((state) => state.formInProgress);

  const showMhaStatusConfirmation = (itemName: string) =>
    navigate(
      routeFns.formContextPageEditMhaStatusDialog(
        formContext.id,
        formContext.patientId,
        { itemName },
      ),
    );

  const showFinaliseConfirmation = (itemName: string) =>
    navigate(
      routeFns.formContextPageConfirmFinaliseDialog(
        formContext.id,
        formContext.patientId,
        { itemName },
      ),
    );

  const assessmentName = t("pages.mcaAssessment.forms");

  return (
    <Box
      data-testid="form-context:mca-assessment"
      data-formcontextid={formContext.id}
    >
      {pdfViewFormId && (
        <FormContextPdfViewer
          inModal={true}
          forms={previewableForms(formContext.forms)}
          setFormId={setPdfViewFormId}
          formId={pdfViewFormId}
        />
      )}

      <Box sx={{ mb: 3 }}>
        {patientTimeline && (
          <PatientBanner
            patient={patientTimeline.patient}
            nhsNumber={patientTimeline.patient.nhsNumber ?? undefined}
            disableSticky
            isHorizontalLineHidden
            patientTimeline={patientTimeline}
            reloadPatientTimeline={reloadPatientTimeline}
            onReconcileClick={onReconcileClick}
          />
        )}
      </Box>

      <WorkItemStatusCard
        formContext={formContext}
        reloadFormContext={reloadFormContext}
        user={user}
      />

      {formContext.activeTeamworkWorkItem &&
        awaitingSignature &&
        awaitingSignature.latestVersion.data.length > 0 && (
          <PatientDemographicsOutdatedBanner
            patient={formContext.patient}
            currentValues={
              awaitingSignature.latestVersion.data[0].data as Record<
                string,
                unknown
              >
            }
          />
        )}

      {promptTemplateId && canStartPromptedForm && (
        <McaContextualPrompt
          formContext={formContext}
          templateId={promptTemplateId}
        />
      )}

      {canFinaliseAssessment && (
        <Box sx={{ mb: 3 }}>
          <Banner
            title={t("pages.mcaAssessment.scrutinyBanner")}
            rightButtonLabel={"Complete"}
            rightButtonTestId="mark-scrutiny-complete-button"
            onRightButton={() =>
              userCanEditMhaStatus(user)
                ? showMhaStatusConfirmation(assessmentName)
                : showFinaliseConfirmation(assessmentName)
            }
            bannerType={BannerList.INFO}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <FormTitle
          subtitleText={t("pages.mcaAssessment.forms")}
          subTitleTextFontSize={2}
          hasContainerMarginBottom={false}
        />
        <ExpandMore
          role="button"
          isExpanded={showForms}
          onClick={() => setShowForms(!showForms)}
          aria-expanded={showForms}
          aria-label={t("components.cardExtended.expandMoreLabel")}
        >
          <ExpandMoreIcon sx={{ color: "primary.main" }} />
        </ExpandMore>
      </Box>

      {showForms &&
        forms.map((form) => (
          <StandaloneFormRow
            key={form.id}
            testId={`mca-form-row:${form.formTemplate.id}`}
            formContext={formContext}
            form={form}
            user={user}
            setPdfViewFormId={setPdfViewFormId}
            showDeleteConfirmation={(formId: UUID) =>
              navigate(
                routeFns.formContextPageConfirmDeleteDraftDialog(
                  formContext.id,
                  formContext.patientId,
                  { draftFormId: formId },
                ),
              )
            }
            showFinaliseConfirmation={showFinaliseConfirmation}
            showMhaStatusConfirmation={showMhaStatusConfirmation}
            showRequestDeMinimisDialog={(formId, formTemplateId) =>
              navigate(
                routeFns.formContextPageRequestDeMinimisDialog(
                  formContext.id,
                  formContext.patientId,
                  { formId, formTemplateId },
                ),
              )
            }
            reloadFormContext={reloadFormContext}
            lastMerged={merged?.lastMerged}
            offerScrutiny={false}
          />
        ))}

      <ParticipantsList formContext={formContext} />

      <FormFooterSection
        hideSubmit={!formContext.activeTeamworkWorkItem}
        isSticky={true}
        saveLabel={t("buttonLabels.send")}
        disableSubmit={
          !formContext.activeTeamworkWorkItem ||
          !["accepted", "completed"].includes(
            formContext.activeTeamworkWorkItem.status,
          )
        }
        onSave={() => {
          // Offer whichever step the assessment is actually waiting on: a form
          // still to be signed, otherwise scrutiny of the finished assessment.
          navigate(
            routeFns.formContextPageSendRequest(
              formContext.id,
              formContext.patientId,
              awaitingSignature
                ? {
                    requestType: "complete",
                    formId: awaitingSignature.form.id,
                    formPart:
                      awaitingSignature.latestVersion.data.length.toString(),
                  }
                : { requestType: "finalise" },
            ),
          );
        }}
        discardLabel={t("buttonLabels.activityLog")}
        onCancel={() => {
          navigate(
            routeFns.formContextPageActivityLog(
              formContext.id,
              formContext.patientId,
            ),
          );
        }}
      />
    </Box>
  );
}
